import React, { useEffect, useState } from "react";

import {
  gatherActiveFilters,
  onChangeQuickFilter,
  onColumnConfigChange,
  onColumnVisibilityChanged,
  onFilterChange,
  onRangePickerChange,
  resetAllActiveFilters,
} from "@src/features/table_filtering/utils/base_state_handler_utils";
import {
  clearCustomTableFilterProperty,
  clearFilter,
  setCustomDateFilter,
} from "@src/services/slices/requestOverviewSlice";
import { useAppDispatch, useAppSelector } from "@src/setupStore";
import { ActiveFilter, REQUEST_OVERVIEW_FILTER_TYPES } from "@src/types";
import { TabItem } from "@src/types/antd_types";
import { ColDef, GridApi } from "ag-grid-community";

import { StaffingRequestsTabs } from "../components/tabs_staffing_requests/tabs_staffing_requests";

interface UseStaffingRequestStateChangeHandlerProps {
  gridApi: React.MutableRefObject<GridApi>;
  columnDefs: ColDef[];
  filterCallBack: () => void;
}

interface UseStaffingRequestStateChangeHandlerResponse {
  activeFilters: ActiveFilter[];
  resetActiveFilters: (key?: string, value?: string) => void;
  setTableRendered: () => void;
}

export const useStaffingRequestStateChangeHandler = ({
  gridApi,
  columnDefs,
  filterCallBack,
}: UseStaffingRequestStateChangeHandlerProps): UseStaffingRequestStateChangeHandlerResponse => {
  const [isTableRendered, setIsTableRendered] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const dispatch = useAppDispatch();

  const {
    requestOverviewTableState: {
      quickFilter,
      customTableFilters,
      customDateFilters,
      currentTabConfig,
      columns,
      columnConfigs,
    },
  } = useAppSelector((state) => state.requestOverviewSlice);

  /**
   * Load filter state from store to grid
   */
  useEffect(() => {
    if (!gridApi.current && !isTableRendered) return;

    onChangeQuickFilter(quickFilter, gridApi.current, filterCallBack);
    onColumnVisibilityChanged(columns, gridApi.current, columnDefs);
    onRangePickerChange(
      customDateFilters.roleRequestStart,
      REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_START,
      gridApi.current,
      filterCallBack
    );
    onRangePickerChange(
      customDateFilters.requestSentDate,
      REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_SENT,
      gridApi.current,
      filterCallBack
    );
    onTabChange(currentTabConfig, gridApi.current);

    for (const key of Object.keys(customTableFilters)) {
      const values: string[] | boolean = customTableFilters[key];

      if (Array.isArray(values)) {
        onFilterChange(values, key, gridApi.current, filterCallBack);
      } else if (typeof values === "boolean") {
        onFilterChange(
          [values.toString()],
          key,
          gridApi.current,
          filterCallBack
        );
      }
    }
    onColumnConfigChange(columnConfigs, gridApi.current);

    const activeFilters: ActiveFilter[] = gatherActiveFilters(
      customTableFilters,
      customDateFilters,
      gridApi.current
    );
    setActiveFilters(activeFilters);
  }, [
    columns,
    quickFilter,
    customTableFilters,
    customDateFilters,
    currentTabConfig,
    gridApi.current,
    isTableRendered,
    columnConfigs,
  ]);

  function onTabChange(currentTabConfig: TabItem, gridApi: GridApi) {
    if (currentTabConfig?.key === StaffingRequestsTabs.REQUIRED_ACTION) {
      onFilterChange(
        ["true"],
        REQUEST_OVERVIEW_FILTER_TYPES.REQUIRED_ACTION,
        gridApi,
        null
      );
    } else if (currentTabConfig?.key === StaffingRequestsTabs.ALL) {
      onFilterChange(
        ["true", "false"],
        REQUEST_OVERVIEW_FILTER_TYPES.REQUIRED_ACTION,
        gridApi,
        null
      );
    }
  }

  function resetActiveFilterByKey(key: string, value: string) {
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}\s-\s\d{2}\/\d{2}\/\d{4}$/;
    if (dateRegex.test(value)) {
      dispatch(
        setCustomDateFilter({
          ...customDateFilters,
          [key]: null,
        })
      );
      onRangePickerChange(null, key, gridApi.current, filterCallBack);
    } else {
      const valueToBeCleared = { key, value };
      dispatch(clearCustomTableFilterProperty(valueToBeCleared));
    }
  }

  function resetActiveFilters(key?: string, value?: string) {
    if (key !== undefined && value !== undefined) {
      resetActiveFilterByKey(key, value);
    } else {
      onRangePickerChange(
        null,
        REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_START,
        gridApi.current,
        filterCallBack
      );
      onRangePickerChange(
        null,
        REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_SENT,
        gridApi.current,
        filterCallBack
      );
      resetAllActiveFilters(gridApi.current);
      onTabChange(currentTabConfig, gridApi.current);
      dispatch(clearFilter());
    }

    filterCallBack();
  }

  return {
    activeFilters,
    resetActiveFilters,
    setTableRendered: () => setIsTableRendered(true),
  };
};
